// ReceitaTooltip.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";

// ==== helpers ====
const onlyDigits = (s = "") => (s || "").replace(/\D/g, "");
function isValidCNPJ(cnpj) {
  const s = onlyDigits(cnpj);
  if (s.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(s)) return false;
  const calcDV = (nums, weights) => {
    const sum = nums
      .split("")
      .reduce((acc, n, i) => acc + parseInt(n, 10) * weights[i], 0);
    const mod = sum % 11;
    return mod < 2 ? 0 : 11 - mod;
  };
  const base12 = s.slice(0, 12);
  const dv1 = calcDV(base12, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  const dv2 = calcDV(base12 + dv1, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  return s === base12 + String(dv1) + String(dv2);
}

function InfoRow({ label, children }) {
  return (
    <div className="flex gap-2 text-sm">
      <div className="w-44 shrink-0 text-gray-500">{label}</div>
      <div className="text-gray-900">{children || "—"}</div>
    </div>
  );
}

// Spinner simples (Tailwind)
function Spinner({ size = 16, className = "" }) {
  const px = `${size}px`;
  return (
    <span
      className={`inline-block rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin ${className}`}
      style={{ width: px, height: px }}
      aria-hidden="true"
    />
  );
}

// ==== componente principal ====
export default function ReceitaTooltip({
  cnpj,
  children, // opcional: trigger custom
  className = "",
  side = "bottom", // 'bottom' | 'top'
  align = "start", // 'start' | 'center' | 'end'
}) {
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const closeTimer = useRef(null);
  const abortRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const valid = useMemo(() => isValidCNPJ(cnpj), [cnpj]);

  // posicionamento
  const [pos, setPos] = useState({ top: 0, left: 0, width: 450 });
  const updatePosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const panelW = 450;
    let left;
    if (align === "start")
      left = Math.max(8, Math.min(rect.left, window.innerWidth - panelW - 8));
    else if (align === "end")
      left = Math.max(
        8,
        Math.min(rect.right - panelW, window.innerWidth - panelW - 8)
      );
    else
      left = Math.max(
        8,
        Math.min(
          rect.left + rect.width / 2 - panelW / 2,
          window.innerWidth - panelW - 8
        )
      );

    const gap = 8;
    const top = side === "top" ? rect.top - gap : rect.bottom + gap;

    setPos({ top, left, width: panelW });
  };

  // abrir + fetch on-demand
  const openWithFetch = () => {
    if (!valid) return;
    clearTimeout(closeTimer.current);
    setOpen(true);
    updatePosition();

    if (!data && !loading && !error) {
      setLoading(true);
      setError(null);
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      fetch(`https://api.opencnpj.org/${onlyDigits(cnpj)}`, {
        method: "GET",
        signal: controller.signal,
      })
        .then(async (res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const json = await res.json();
          setData(json);
        })
        .catch((e) => {
          if (e.name !== "AbortError") setError(e);
        })
        .finally(() => setLoading(false));
    }
  };

  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 200);
  };

  // manter aberto quando mouse sobre TRIGGER ou PAINEL
  const onEnter = () => {
    clearTimeout(closeTimer.current);
    if (!open) openWithFetch();
  };
  const onLeave = () => {
    scheduleClose();
  };

  // fechar com ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // reposiciona em scroll/resize
  useEffect(() => {
    if (!open) return;
    const fn = () => updatePosition();
    window.addEventListener("scroll", fn, true);
    window.addEventListener("resize", fn);
    return () => {
      window.removeEventListener("scroll", fn, true);
      window.removeEventListener("resize", fn);
    };
  }, [open, side, align]);

  useEffect(() => () => abortRef.current?.abort(), []);

  // trigger default
  const Trigger = (
    <button
      type="button"
      ref={triggerRef}
      className={`inline-flex items-center gap-2 px-2 py-1 rounded-md border text-xs ${
        valid
          ? "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
          : "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
      } ${className}`}
      disabled={!valid || loading}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={onEnter}
      onBlur={scheduleClose}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-busy={loading ? "true" : "false"}
      title={valid ? "Ver Dados da Receita Federal" : "CNPJ inválido"}
    >
      {loading && <Spinner size={12} />}
      Ver Dados da Receita
    </button>
  );

  const content = (
    <div
      ref={panelRef}
      role="dialog"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={onEnter}
      onBlur={scheduleClose}
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        width: pos.width,
        zIndex: 10000,
      }}
      className="shadow-xl border border-gray-200 rounded-lg bg-white"
      aria-busy={loading ? "true" : "false"}
    >
      <div className="px-3 py-2 border-b bg-gray-50 rounded-t-lg flex items-center justify-between">
        <div className="text-sm font-semibold text-gray-800">
          Dados da Receita Federal
        </div>
        <button
          className="text-gray-500 hover:text-gray-700 text-sm"
          onClick={() => setOpen(false)}
          aria-label="Fechar"
        >
          ✕
        </button>
      </div>

      <div className="max-h-[360px] overflow-y-auto p-3 space-y-2">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Spinner size={16} />
            Carregando…
          </div>
        )}

        {error && (
          <div className="text-sm text-red-600">
            Não foi possível obter os dados ({String(error.message || error)}).
          </div>
        )}

        {!loading && !error && data && (
          <>
            <InfoRow label="CNPJ">{data.cnpj}</InfoRow>
            <InfoRow label="Razão social">{data.razao_social}</InfoRow>
            <InfoRow label="Nome fantasia">{data.nome_fantasia || "—"}</InfoRow>
            <InfoRow label="Situação">{data.situacao_cadastral}</InfoRow>
            <InfoRow label="Desde">{data.data_situacao_cadastral}</InfoRow>
            <InfoRow label="Matriz/Filial">{data.matriz_filial}</InfoRow>
            <InfoRow label="Início atividade">
              {data.data_inicio_atividade}
            </InfoRow>
            <InfoRow label="CNAE principal">{data.cnae_principal}</InfoRow>
            <InfoRow label="CNAEs secundários">
              {Array.isArray(data.cnaes_secundarios) &&
              data.cnaes_secundarios.length ? (
                <div className="flex flex-wrap gap-1">
                  {data.cnaes_secundarios.map((c) => (
                    <span
                      key={c}
                      className="px-1.5 py-0.5 rounded bg-gray-100 border border-gray-200 text-xs"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              ) : (
                "—"
              )}
            </InfoRow>
            <InfoRow label="Natureza jurídica">
              {data.natureza_juridica}
            </InfoRow>
            <InfoRow label="Endereço">
              {[data.logradouro, data.numero, data.complemento]
                .filter(Boolean)
                .join(", ")}
              {" — "}
              {[data.bairro, data.municipio, data.uf]
                .filter(Boolean)
                .join(" - ")}
              {data.cep ? ` • CEP ${data.cep}` : ""}
            </InfoRow>
            <InfoRow label="E-mail">{data.email || "—"}</InfoRow>
            <InfoRow label="Telefones">
              {Array.isArray(data.telefones) && data.telefones.length
                ? data.telefones.map((t, i) => (
                    <span key={i} className="mr-2">
                      ({t.ddd}) {t.numero}
                      {t.is_fax ? " (fax)" : ""}
                    </span>
                  ))
                : "—"}
            </InfoRow>
            <InfoRow label="Capital social">{data.capital_social}</InfoRow>
            <InfoRow label="Porte">{data.porte_empresa}</InfoRow>
            <InfoRow label="Simples">
              {data.opcao_simples === "S" ? "Optante" : "Não optante"}
            </InfoRow>
            <InfoRow label="Desde (Simples)">{data.data_opcao_simples}</InfoRow>
            <InfoRow label="MEI">
              {data.opcao_mei === "S" ? "Sim" : "Não"}
            </InfoRow>
            {Array.isArray(data.QSA) && data.QSA.length > 0 && (
              <div>
                <div className="w-44 shrink-0 text-gray-500 mb-1">
                  Quadro societário
                </div>
                <div className="space-y-2">
                  {data.QSA.map((s, i) => (
                    <div key={i} className="border border-gray-200 rounded p-2">
                      <div className="text-sm font-medium">{s.nome_socio}</div>
                      <div className="text-xs text-gray-600">
                        {s.qualificacao_socio} • {s.identificador_socio} •
                        Ingresso: {s.data_entrada_sociedade}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {!loading && !error && !data && (
          <div className="text-sm text-gray-600">Sem dados.</div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Trigger */}
      <span
        ref={triggerRef}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        onFocus={onEnter}
        onBlur={scheduleClose}
        className="inline-flex"
      >
        {children ?? Trigger}
      </span>

      {/* Painel em portal */}
      {open && ReactDOM.createPortal(content, document.body)}

      {/* estilos utilitários (Tailwind opcional) */}
      <style>{`
        .rf-scroll::-webkit-scrollbar { width: 8px; }
        .rf-scroll::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 9999px; }
      `}</style>
    </>
  );
}
